const activatePanel = (buttons, panels, value, scrollTarget) => {
  if (!value) return;

  let didActivate = false;
  buttons.forEach((button) => {
    const isActive =
      button.dataset.yogProblem === value ||
      button.dataset.yogTab === value ||
      button.dataset.yogMilk === value;

    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-selected', String(isActive));
    didActivate = didActivate || isActive;
  });

  panels.forEach((panel) => {
    const isActive =
      panel.dataset.yogPanel === value ||
      panel.dataset.yogTabPanel === value ||
      panel.dataset.yogMilkPanel === value;

    panel.hidden = !isActive;
  });

  if (didActivate && scrollTarget) {
    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const parseCalculatorStrings = (calculator) => {
  const node = calculator.querySelector('[data-yog-calculator-strings]');
  if (!node) return null;

  try {
    return JSON.parse(node.textContent);
  } catch (_error) {
    return null;
  }
};

const initCalculator = (calculator) => {
  if (!calculator || calculator.dataset.sealYogCalculatorReady === 'true') return;

  const strings = parseCalculatorStrings(calculator);
  if (!strings) return;

  calculator.dataset.sealYogCalculatorReady = 'true';

  const fullGlasses = Number(calculator.dataset.fullGlasses || 6);
  const fullLiters = Number(calculator.dataset.fullLiters || 1.2);
  const litersPerGlass = fullLiters / fullGlasses;
  const locale = strings.locale || document.documentElement.lang || 'de';
  const range = calculator.querySelector('[data-yog-calc-range]');
  const amount = calculator.querySelector('[data-yog-calc-amount]');
  const equivalent = calculator.querySelector('[data-yog-calc-equivalent]');
  const result = calculator.querySelector('[data-yog-calc-result]');
  const percent = calculator.querySelector('[data-yog-calc-percent]');
  const volume = calculator.querySelector('[data-yog-calc-volume]');
  const noteTitle = calculator.querySelector('[data-yog-calc-note-title]');
  const note = calculator.querySelector('[data-yog-calc-note]');

  const formatNumber = (value, digits = 1) =>
    value.toLocaleString(locale, {
      maximumFractionDigits: digits,
      minimumFractionDigits: 0,
    });

  const glassLabel = (glasses) => {
    if (glasses === 1) return strings.glassOne;
    if (locale === 'cs' && glasses > 1 && glasses < 5) {
      return strings.glassFew.replace('__COUNT__', formatNumber(glasses, 0));
    }
    return strings.glassMany.replace('__COUNT__', formatNumber(glasses, 0));
  };

  const formatPacket = (sachets) => {
    const sixths = Math.round(sachets * 6);
    const closeToSixth = Math.abs(sachets - sixths / 6) < 0.025;

    if (closeToSixth && strings.packets?.[String(sixths)]) return strings.packets[String(sixths)];
    return strings.packetDecimal.replace('__AMOUNT__', formatNumber(sachets, 2));
  };

  const updateCalculator = () => {
    if (!range) return;

    const glasses = Math.min(fullGlasses, Math.max(1, Math.round(Number(range.value)) || fullGlasses));
    const liters = glasses * litersPerGlass;
    const sachets = glasses / fullGlasses;
    const percentage = Math.round(sachets * 100);

    range.value = String(glasses);

    if (amount) amount.textContent = glassLabel(glasses);
    if (equivalent) equivalent.textContent = `= ${formatNumber(liters, 3)} l`;
    if (result) result.textContent = formatPacket(sachets);
    if (percent) percent.textContent = `${percentage} %`;
    if (volume) volume.textContent = `${formatNumber(liters, 3)} l`;

    if (noteTitle && note) {
      if (sachets >= 0.98) {
        noteTitle.textContent = strings.notes.standardTitle;
        note.textContent = strings.notes.standardText;
      } else if (sachets < 0.25) {
        noteTitle.textContent = strings.notes.littleTitle;
        note.textContent = strings.notes.littleText;
      } else {
        noteTitle.textContent = strings.notes.partialTitle;
        note.textContent = strings.notes.partialText;
      }
    }
  };

  if (range) range.addEventListener('input', updateCalculator);
  updateCalculator();
};

const initSection = (section) => {
  if (!section || section.dataset.sealYogHelpReady === 'true') return;
  section.dataset.sealYogHelpReady = 'true';

  const problemButtons = Array.from(section.querySelectorAll('[data-yog-problem]'));
  const problemPanels = Array.from(section.querySelectorAll('[data-yog-panel]'));
  const problemSection = section.querySelector('#soforthilfe');

  problemButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const value = button.dataset.yogProblem;
      activatePanel(problemButtons, problemPanels, value);
      if (value) history.replaceState(null, '', `#${value}`);
    });
  });

  const tabButtons = Array.from(section.querySelectorAll('[data-yog-tab]'));
  const tabPanels = Array.from(section.querySelectorAll('[data-yog-tab-panel]'));
  tabButtons.forEach((button) => {
    button.addEventListener('click', () => activatePanel(tabButtons, tabPanels, button.dataset.yogTab));
  });

  const milkButtons = Array.from(section.querySelectorAll('[data-yog-milk]'));
  const milkPanels = Array.from(section.querySelectorAll('[data-yog-milk-panel]'));
  milkButtons.forEach((button) => {
    button.addEventListener('click', () => activatePanel(milkButtons, milkPanels, button.dataset.yogMilk));
  });

  initCalculator(section.querySelector('[data-yog-dose-calculator]'));

  const openFromHash = () => {
    const id = window.location.hash ? window.location.hash.slice(1) : '';
    if (!id) return;

    const hasProblem = problemPanels.some((panel) => panel.dataset.yogPanel === id);
    if (hasProblem) activatePanel(problemButtons, problemPanels, id, problemSection);
  };

  openFromHash();
  window.addEventListener('hashchange', openFromHash);
};

const initAll = () => {
  document.querySelectorAll('[data-seal-yog-it-help]').forEach(initSection);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target?.querySelector?.('[data-seal-yog-it-help]');
  if (section) {
    section.dataset.sealYogHelpReady = 'false';
    initSection(section);
  }
});
